/* RESPONSIBLE TEAM: team-standalone */
import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';

interface GuidanceContextItem {
  type: 'guidance';
  id: string;
  title: string | null;
  contextKey: string;
  category: string | null;
  prompt: string | null;
}

export default class GuidanceAgentSidebarService extends Service {
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;

  @tracked isOpen = false;
  @tracked guidanceContexts: GuidanceContextItem[] = [];
  @tracked lastChatId: string | null = null;

  private unsubscribers: Array<() => void> = [];

  init() {
    super.init();
    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent('guidance-agent:close-sidebar', () =>
        this.close(),
      ),
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'guidance-agent:chat-id-changed',
        (data: Record<string, unknown>) => {
          this.lastChatId = data['chatId'] as string;
        },
      ),
    );
  }

  willDestroy() {
    super.willDestroy();
    this.unsubscribers.forEach((unsub) => unsub());
  }

  openWithGuideline(guideline: {
    id: string;
    title: string | null;
    category: string | null;
    prompt: string | null;
  }) {
    let context: GuidanceContextItem = {
      type: 'guidance',
      id: guideline.id,
      title: guideline.title,
      contextKey: guideline.id,
      category: guideline.category,
      prompt: guideline.prompt,
    };
    this.guidanceContexts = [context];

    if (this.isOpen) {
      // Sidebar already mounted — props won't update through render-react-component
      // (shadow DOM is only created once), so push context via bridge event.
      this.reactTeammateAppIsland.dispatchTeammateAppEvent('guidance-agent:update-context', {
        context,
      });
    }

    this.isOpen = true;
  }

  close() {
    this.isOpen = false;
    this.guidanceContexts = [];
  }
}

declare module '@ember/service' {
  interface Registry {
    'guidance-agent-sidebar': GuidanceAgentSidebarService;
    guidanceAgentSidebar: GuidanceAgentSidebarService;
  }
}
